import {
  DateFns,
  FP,
  FPRx,
  RemoteData,
  Rx,
  RxOperators,
  Simplify,
  StencilStore,
} from "../deps"

import * as ServiceShopify from "../service-shopify/service-shopify"

import * as API from "./api"
import * as APITypes from "./api-types"
import * as Errors from "../errors"
import * as Events from "../events/events"

import * as ErrorsService from "../service-errors/service-errors"
import * as ServiceIdentity from "../service-identity/service-identity"

export {
  PurchaseState,
  PurchaseResaleOfferPricingDescriptor,
  PurchaseResaleDiscountState,
} from "./api-types"

import { getViewerCountry } from "./viewer-location"
export { getViewerCountry }

export type ShopId = string & { readonly _tag?: unique symbol }
export type CustomerId = string & { readonly _tag?: unique symbol }
export type CustomerEmail = string & { readonly _tag?: unique symbol }

export type CustomerIdentity = FP.option.Option<
  readonly [shop: ShopId, customer: FP.either.Either<CustomerId, CustomerEmail>]
>

export type CustomerData = Simplify<
  Pick<
    APITypes.CustomerDataQuery["customer"],
    "id" | "customerId" | "globalCustomerId"
  > & {
    purchases: readonly Purchase[]
    discount: Discount | null
  }
>

export type CustomerRemoteData = RemoteData.RemoteData<Error, CustomerData>

export type Purchase = Simplify<
  APITypes.CustomerDataQuery["customer"]["purchases"]["edges"][0]["node"] & {
    /** Sample purchase item for signed out users */
    isSample?: true
  }
>

export type Discount =
  APITypes.CustomerDataQuery["customer"]["discounts"]["edges"][0]["node"]

export type MonetaryAmount = APITypes.MonetaryAmount

const customerIdentity = new Rx.BehaviorSubject<CustomerIdentity>(
  FP.option.none
)

const store = StencilStore.createStore({
  cartItemCount: 0,
  cartTotal: {
    currency: "GBP",
    value: 0,
  } as MonetaryAmount,
  customerId: null as string | null,
  customerData: RemoteData.initial as RemoteData.RemoteData<
    Error,
    CustomerData
  >,
})

export function setCartItemCount(value: number) {
  return store.set("cartItemCount", value)
}

export function getCartItemCount(): number {
  return store.get("cartItemCount")
}

export function setCartTotal(
  value: number,
  currency: string = "GBP"
): MonetaryAmount {
  const amount: MonetaryAmount = {
    currency,
    value,
  }
  store.set("cartTotal", amount)
  return amount
}

export function getCartTotal(): MonetaryAmount {
  return store.get("cartTotal")
}

const isCartTotalNonEmpty = FP.function.flow(
  getCartTotal,
  (total) => total.value > 0
)

export function getCustomerData() {
  return store.get("customerData")
}

export const getSellalongDiscountTotal: () => number = FP.function.flow(
  getSellalongDiscountAmount,
  FP.option.fold(FP.function.constant(0), (amount) => amount.value)
)

export function getSellalongDiscountAmount(): FP.option.Option<APITypes.MonetaryAmount> {
  return FP.function.pipe(
    getCustomerData(),
    FP.option.fromPredicate(RemoteData.isSuccess),
    FP.option.chainNullableK((customerData) => customerData.value.discount),
    FP.option.filter(
      (discount) =>
        discount.state === APITypes.PurchaseResaleDiscountState.Available &&
        discount.discountAmount.value > 0
    ),
    FP.option.map((discount) => discount.discountAmount)
  )
}

export function getSellalongDiscountRolledoverTotal(): FP.option.Option<APITypes.MonetaryAmount> {
  return FP.function.pipe(
    getCustomerData(),
    FP.option.fromPredicate(RemoteData.isSuccess),
    FP.option.chainNullableK((customerData) => customerData.value.discount),
    FP.option.filter(
      (discount) =>
        discount.state === APITypes.PurchaseResaleDiscountState.Available
    ),
    FP.option.chainNullableK((discount) => discount.creditRolledOver),
    FP.option.filter((creditRolledOver) => creditRolledOver.value > 0)
  )
}

export function getCartTotalAfterDiscount(): MonetaryAmount {
  const cartTotal = getCartTotal()
  return {
    currency: cartTotal.currency,
    value: Math.max(0, cartTotal.value - getSellalongDiscountTotal()),
  }
}

export function getMaxSellbackAmount(): APITypes.MonetaryAmount {
  return FP.function.pipe(
    getCustomerData(),
    FP.option.fromPredicate(RemoteData.isSuccess),
    FP.option.fold(
      () => 0,
      FP.function.flow(
        (customerData) => customerData.value.purchases,
        FP.readonlyArray.foldMap(FP.number.MonoidSum)(
          (purchase) => purchase.offer.price?.value || 0
        )
      )
    ),
    (value) => ({ currency: "GBP", value })
  )
}

export function hasCustomer() {
  return store.get("customerId") !== null
}

export function getInvitedUserEmail() {
  return (
    (window.sessionStorage &&
      window.localStorage &&
      window.sessionStorage.getItem &&
      window.localStorage.getItem &&
      (window.sessionStorage.getItem("sa_email") ||
        window.localStorage.getItem("sa_email"))) ||
    null
  )
}

/** User has entered via an invite link, email may not exist for signed-out journeys */
export function isInviteLinkReferral() {
  const value =
    (window.sessionStorage &&
      window.localStorage &&
      window.sessionStorage.getItem &&
      window.localStorage.getItem &&
      (window.sessionStorage.getItem("sa_invited") ||
        window.localStorage.getItem("sa_invited"))) ||
    null
  return value === "true"
}

export function toSellalongUserId(
  shopId: ServiceShopify.ShopId,
  customerId: string
) {
  return `sellalong://shopify/${shopId}/customer/${customerId}`
}

const purchasesSortOrd = FP.function.pipe(
  FP.ord.tuple(FP.ord.reverse(FP.number.Ord), FP.string.Ord),
  FP.ord.contramap((purchase: Purchase) =>
    FP.function.pipe(
      purchase,
      FP.option.fromPredicate(
        (purchase) =>
          purchase.state === APITypes.PurchaseState.Owned ||
          purchase.state === APITypes.PurchaseState.ToSell
      ),
      FP.option.chainNullableK((purchase) => purchase.offer.price?.value),
      FP.option.getOrElse(() => 0),
      (price) => [price, purchase.orderDate] as const
    )
  )
)

export const recordEvent: (
  event: Required<
    Pick<
      APITypes.CustomerEventInput,
      | "eventKind"
      | "eventState"
      | "eventTarget"
      | "sitePath"
      | "uiEnabled"
      | "shopId"
    >
  >
) => FP.task.Task<void> = (event) =>
  FP.function.pipe(
    getCustomerData(),
    RemoteData.toOption,
    FP.option.foldW(
      () => ({
        localCustomerId: null,
        customerId: null,
        globalCustomerId: null,
      }),
      ({ id, customerId }) => ({
        localCustomerId: id,
        customerId,
        globalCustomerId: customerId,
      })
    ),

    FP.taskEither.of,
    FP.taskEither.bind("viewerCountry", getViewerCountry),

    FP.taskEither.map(
      (ids): APITypes.CustomerEventInput => ({
        ...event,
        ...ids,

        createdDate: DateFns.formatISO(
          Date.now()
        ) as APITypes.CustomerEventInput["createdDate"],

        anonymousUserId: ServiceIdentity.getAnonId(),
        customerInvitedByEmail: isInviteLinkReferral(),
        cartTotal: getCartTotal().value,
        sellbackTotal: getSellalongDiscountTotal(),
        sellbackCartDiscountTotal: getCartTotalAfterDiscount().value,
        cartItemCount: getCartItemCount(),
      })
    ),

    FP.taskEither.chain(
      FP.function.flow(
        API.recordEvent,
        FP.task.map(
          RemoteData.toEither(
            () => new Error("RecordEvent mutation failed: initial"),
            () => new Error("RecordEvent mutation failed: pending")
          )
        )
      )
    ),

    FP.task.map(FP.either.getOrElse(ErrorsService.recordError))
  )

/**
 * Update cart with attributes for recording deferred discounts
 */
const updateCartAttributes: (
  observable: Rx.Observable<CustomerData>
) => Rx.Observable<void> = FP.function.flow(
  FPRx.observable.filterMap((customerData) =>
    FP.function.pipe(
      customerData.discount,
      FP.option.fromNullable,

      FP.option.filter(
        (discount) =>
          discount.state === APITypes.PurchaseResaleDiscountState.Available
      ),

      FP.option.bindTo("discount"),
      FP.option.bind("sellbackCount", () =>
        FP.function.pipe(
          customerData.purchases,
          FP.readonlyArray.reduce(0, (acc, curr) =>
            curr.state === APITypes.PurchaseState.ToSell ? acc + 1 : acc
          ),
          FP.option.of
        )
      )
    )
  ),

  RxOperators.distinctUntilChanged(
    (previous, current) =>
      previous.discount.id === current.discount.id &&
      previous.discount.discountAmount.value ===
        current.discount.discountAmount.value &&
      previous.sellbackCount === current.sellbackCount
  ),

  RxOperators.map(({ discount, sellbackCount }) => ({
    attributes: {
      sellalong_discount_id: discount.id,
      sellalong_discount_amount: discount.discountAmount.value.toFixed(2),
      sellalong_discount_code: discount.discountCode,
      sellalong_sellback_count: sellbackCount.toString(),
      sellalong_correlation_id: ServiceIdentity.getAnonId(),
    },
  })),

  FPRx.observable.chain(
    FP.function.flow(
      ServiceShopify.updateCart,

      FP.task.map(
        FP.either.fold(
          FP.function.flow(ErrorsService.recordError, FP.function.constVoid),
          FP.function.constVoid
        )
      ),
      FPRx.observable.fromTask
    )
  )
)

/**
 * Rollover discount credit if needed
 */
const rolloverDiscount: (
  observable: Rx.Observable<CustomerData>
) => Rx.Observable<void> = FP.function.flow(
  FPRx.observable.filterMap((customerData) =>
    FP.function.pipe(
      customerData.discount,
      FP.option.fromNullable,
      FP.option.filter(
        (discount) =>
          discount.state !== APITypes.PurchaseResaleDiscountState.Available
      ),
      FP.option.chainNullableK((discount) => discount.claimedOn),
      FP.option.filter(
        (claimedDetails) => claimedDetails.amountRemaining.value > 0
      ),
      FP.option.filter(isCartTotalNonEmpty),
      FP.option.map(() => ({
        customerId: customerData.id,
        globalCustomerId: customerData.globalCustomerId,
      }))
    )
  ),

  FPRx.observable.chain(
    FP.function.flow(
      (inputs) => API.rolloverDiscount(inputs),
      FP.task.map(
        FP.function.flow(
          RemoteData.mapLeft(ErrorsService.recordError),
          FP.function.constVoid
        )
      ),
      FPRx.observable.fromTask
    )
  )
)

/**
 * Apply discount code to cart automatically
 */
const applyDiscountToCart: (
  enable: boolean
) => (observable: Rx.Observable<CustomerData>) => Rx.Observable<void> = (
  enable
) =>
  FP.function.flow(
    FPRx.observable.filterMap(
      FP.function.flow(
        (customerData) => customerData.discount,
        FP.option.fromNullable,
        FP.option.filter(() => enable),

        // only apply discounts when cart has value as discounts wont work on a free checkout
        FP.option.filter(isCartTotalNonEmpty),

        FP.option.filter(
          (discount) =>
            discount.state === APITypes.PurchaseResaleDiscountState.Available
        )
      )
    ),

    RxOperators.distinctUntilChanged(
      (previous, current) =>
        previous.id === current.id &&
        (previous.discountAmount.value === current.discountAmount.value ||
          (previous.discountAmount.value > 0 &&
            current.discountAmount.value > 0))
    ),

    FPRx.observable.chain(
      FP.function.flow(
        FP.option.fromPredicate(
          (discount) => discount.discountAmount.value > 0
        ),
        FP.option.map((discount) => discount.discountCode),
        FP.option.fold(
          ServiceShopify.clearDiscountCode,
          ServiceShopify.applyDiscountCode
        ),
        ErrorsService.recordTaskEitherError,
        FPRx.observable.fromTask
      )
    )
  )

const toCustomerData: (input: API.CustomerDataQueryResponse) => CustomerData =
  ({ purchases, discounts, ...rest }): CustomerData => ({
    ...rest,
    purchases: FP.function.pipe(
      purchases.edges,

      FP.readonlyArray.filterMap(
        FP.function.flow(
          (edge) => edge.node,

          // ignore all refunded purchases
          FP.option.fromPredicate(
            (purchase) => purchase.state !== APITypes.PurchaseState.Refunded
          )
        )
      ),

      FP.readonlyArray.sort(purchasesSortOrd)
    ),

    discount: FP.function.pipe(
      discounts.edges,
      FP.readonlyArray.head,
      FP.option.map((edge) => edge.node),
      FP.option.toNullable
    ),
  })

export const identifyCustomer: (input: {
  shopifyShopId: ServiceShopify.ShopId
  shopifyCustomerId: string | null
  email: string | null
}) => FP.taskEither.TaskEither<
  Error,
  FP.option.Option<{
    customerId: string
    globalCustomerId: string
    hasSessionEmail: boolean
  }>
> = ({ shopifyShopId, shopifyCustomerId, email }) =>
  FP.function.pipe(
    shopifyCustomerId,
    FP.option.fromNullable,
    FP.option.filter(FP.predicate.not(FP.string.isEmpty)),
    FP.either.fromOption(
      FP.function.flow(
        () => email,
        FP.option.fromNullable,
        FP.option.filter(FP.predicate.not(FP.string.isEmpty)),
        FP.option.alt(FP.option.fromNullableK(getInvitedUserEmail)),
        FP.option.filter(FP.predicate.not(FP.string.isEmpty))
      )
    ),

    // transform to Option<Either<customerId, email>>
    FP.either.swap,
    FP.either.sequence(FP.option.Applicative),
    FP.option.map((customer) => [shopifyShopId, customer] as const),

    (id) => {
      customerIdentity.next(id)
      return id
    },
    FP.option.fold(
      FP.function.flow(FP.function.constant(FP.option.none), FP.taskEither.of),
      ([_shopifyShopId, emailE]) =>
        FP.function.pipe(
          customerDataEither,

          RxOperators.take(1),
          RxOperators.map(
            FP.either.map((customerData) =>
              FP.option.some({
                customerId: customerData.id,
                globalCustomerId: customerData.globalCustomerId,
                hasSessionEmail: FP.either.isRight(emailE),
              })
            )
          ),
          FPRx.observableEither.toTaskEither,
          FP.taskEither.orElseW(
            FP.function.flow(
              FP.taskEither.fromPredicate(
                Errors.isCustomerNotFoundError,
                FP.function.identity
              ),
              FP.taskEither.map(
                FP.function.flow(
                  ErrorsService.recordError,
                  () => FP.option.none
                )
              )
            )
          )
        )
    )
  )

export const customerRemoteDataOpt: Rx.Observable<
  FP.option.Option<CustomerRemoteData>
> = FP.function.pipe(
  customerIdentity,
  FPRx.observable.chain(
    FP.option.traverse(FPRx.observable.Applicative)(([shopId, customer]) =>
      FP.function.pipe(
        customer,
        FP.either.fold(
          (customerId) => API.watchCustomerDataById(shopId, customerId),
          (email) => API.watchCustomerDataByEmail(shopId, email)
        ),
        RxOperators.map(RemoteData.map(toCustomerData)),
        RxOperators.share()
      )
    )
  )
)

export const customerRemoteData: Rx.Observable<CustomerRemoteData> =
  FP.function.pipe(
    customerRemoteDataOpt,
    FPRx.observable.filterMap(FP.function.identity)
  )

export const customerData: Rx.Observable<CustomerData> = FP.function.pipe(
  customerRemoteData,
  FPRx.observable.filterMap(RemoteData.toOption)
)

export const customerDataEither: Rx.Observable<
  FP.either.Either<Error, CustomerData>
> = FP.function.pipe(
  customerRemoteData,
  FPRx.observable.filterMap(
    RemoteData.fold3(
      () => FP.option.none,
      (error) => FP.option.some(FP.either.left(error)),
      (data) => FP.option.some(FP.either.right(data))
    )
  )
)

export const customerDataErrors: Rx.Observable<Error> = FP.function.pipe(
  customerRemoteData,
  FPRx.observable.filterMap(
    FP.function.flow(
      FP.option.fromPredicate(RemoteData.isFailure),
      FP.option.map((remoteData) => remoteData.error)
    )
  )
)

// subscriptions
customerRemoteData.subscribe((result) => store.set("customerData", result))
customerData.subscribe(({ id, customerId }) => {
  ErrorsService.setUser(id)

  // TODO customerId is now both shopify customer id and sellalong global id - fine for now as only using for customer presence
  store.set("customerId", customerId)
})
customerDataErrors.subscribe(ErrorsService.recordError)

// discount handling
FP.function
  .pipe(
    [
      // Update cart attributes for recording deferred discounts
      updateCartAttributes,

      // Rollover discount credit if needed
      rolloverDiscount,

      // Apply discount code to the cart
      applyDiscountToCart(true),
    ],
    FP.readonlyArray.ap(FP.readonlyArray.of(customerData)),

    FP.readonlyArray.sequence(FPRx.observable.Applicative)
  )
  .subscribe()

// Handle state transitioning
FP.function
  .pipe(
    Events.eventBus,
    RxOperators.filter(Events.isEvent("purchaseStateTransitionIntent")),

    FPRx.observable.chain(
      FP.function.flow(
        ({ purchaseId, futureState }) =>
          API.updateState({
            id: purchaseId,
            state:
              futureState === "OWNED"
                ? APITypes.PurchaseState.Owned
                : APITypes.PurchaseState.ToSell,
          }),
        FPRx.observable.fromTask
      )
    ),

    RxOperators.tap((result) =>
      FP.function.pipe(
        result,
        FP.option.fromPredicate(RemoteData.isSuccess),
        FP.option.fold(FP.function.constVoid, (data) =>
          Events.publish({
            kind: "purchaseStateTransitionAction",
            purchaseId: data.value.id,
            currentState:
              data.value.state === APITypes.PurchaseState.Owned
                ? "OWNED"
                : "TO_SELL",
          })
        )
      )
    )
  )
  .subscribe()

// Detect when any component is removed - likely when an ajax write has occurred when item quantity changes
FP.function
  .pipe(
    Events.eventBus,
    RxOperators.filter(
      FP.predicate.or(Events.isEvent("sellalongComponentRemovedInternal"))(
        // Ensure cart.js is reloaded to update cart count/totals for when first item added to cart overlay
        (event) =>
          Events.isEvent("cartImpressionAction")(event) && !event.fullPage
      )
    ),
    RxOperators.throttleTime(500),
    FPRx.observable.chain(
      FP.function.flow(ServiceShopify.getCart, FPRx.observable.fromTask)
    ),
    FPRx.observable.filterMap(RemoteData.toOption),
    RxOperators.distinctUntilChanged(
      (prev, curr) =>
        prev.item_count === curr.item_count &&
        prev.total_price === curr.total_price
    ),
    RxOperators.tap((response) => {
      setCartItemCount(response.item_count)
      setCartTotal(response.total_price / 100)
      Events.publish({ kind: "cartChangeAction" })
    })
  )
  .subscribe()
